/**
 * The transcription-guided labelling rules, as pure functions.
 *
 * This is the differentiator's logic, kept out of the components so it can be tested without a
 * canvas: what letter a newly drawn box gets, where the cursor goes next, and how a lacuna
 * that covers an unknown number of glyphs is represented.
 *
 * Coordinates never appear in this file. Boxes carry an image-pixel `bbox` produced by
 * `coords.ts`; nothing here does arithmetic on it.
 *
 * ---------------------------------------------------------------------------
 * The lacuna rule (epigraphic ruling, not an implementation convenience)
 * ---------------------------------------------------------------------------
 * A `----` run in a transliteration is **one** `illegible` chip standing for an illegible
 * stretch of *unknown* length. It is not four letters. So the annotator must be able to draw
 * 0, 1 or 5 boxes against a single `illegible` chip and still finish the image.
 *
 * The database enforces `UNIQUE(image_id, expected_token_index)`, so exactly one box can *be*
 * the k-th expected letter. Therefore:
 *
 *  * the **first** box drawn on a lacuna chip takes the chip (`alignment='expected'`);
 *  * every further glyph in that stretch is recorded as `alignment='unexpected'` — a real
 *    insertion, which is the honest description: the stone has a glyph OCIANA's reading does
 *    not enumerate;
 *  * the cursor does **not** auto-advance off a lacuna chip, because the annotator does not
 *    know in advance how many glyphs the stretch holds. They move on with the next-letter key.
 *
 * Drawing zero boxes is expressed by marking the chip not-present, which counts as resolved.
 * See `progress.ts`.
 */

import type { OrientedRect } from './coords';
import type {
  Alignment,
  AnnotationOut,
  AnnotationSource,
  Chirality,
  ReadingDirection,
  TokenOut,
  VocabEntryOut,
} from './api/types';
import { isSymmetric, letterChirality } from './glyphs';

/** Canonical class names the tokenizer emits for things that are not a specific letter. */
export const ILLEGIBLE = 'illegible';
export const UNKNOWN = 'unknown';

/**
 * One box in the editor, in **image pixel space**.
 *
 * `key` is a client-side identity that survives a save: `id` is null until the service assigns
 * one, and reconciling by array position alone would misattribute a box if the payload and the
 * response ever diverged.
 */
export interface DraftBox {
  key: string;
  id: number | null;
  letterClass: number;
  letter: string | null;
  display: string | null;
  /**
   * The box, **oriented**. `angle` is degrees clockwise about its own centre, and is what a
   * straightened view leaves behind on the photograph — see `lib/coords.ts`, which owns every
   * conversion into and out of this.
   */
  bbox: OrientedRect;
  alignment: Alignment;
  expectedTokenIndex: number | null;
  wordIndex: number | null;
  /**
   * **Deprecated** (schema 6). Superseded by `chirality`, and still sent on every save for the
   * length of the expand window because the column is `NOT NULL`. It is no longer defaulted
   * from the record's reading direction — nothing derives it from anything about the record —
   * and it is restated from `chirality` where a boolean can express one. See
   * `mirroredForChirality`.
   */
  mirrored: boolean;
  /** Handedness relative to the stored reference form. `null` means nobody has looked. */
  chirality: Chirality | null;
  uncertain: boolean;
  restored: boolean;
  source: AnnotationSource;
  confidence: number | null;
  notes: string | null;
}

let keyCounter = 0;

export function newKey(): string {
  keyCounter += 1;
  return `b${keyCounter}-${Math.random().toString(36).slice(2, 8)}`;
}

export function isLacunaToken(token: TokenOut | undefined): boolean {
  return token?.letter === ILLEGIBLE || token?.letter === UNKNOWN;
}

export function tokenLabel(token: TokenOut): string {
  if (token.letter === ILLEGIBLE) return '—';
  if (token.letter === UNKNOWN) return '?';
  return token.display;
}

/**
 * What an `unknown` chip is actually standing for, in plain language.
 *
 * `unknown` covers two unrelated things in this corpus, and telling the annotator which one they
 * are looking at changes what they do *(ruling 2026-07-27)*:
 *
 *  * a **bare `s`** — 7 chips — is certainly a letter, and certainly a sibilant. The editor either
 *    dropped the superscript (`TIJ 163` `sqm`, written `s¹qm` in `CH.R207.1`) or declined to assign
 *    the series (`CH.R374.2` writes `s²` and bare `s` on one line). Either way there is a glyph on
 *    the stone. **Box it.** It is not read as `s¹`, because one of those two cases would make that
 *    an invented reading;
 *  * the other four — an `o`, two `<`, and `KJC 201`'s `’` — are most likely typographic noise. The
 *    stray character appears in none of the three translations that have one. Expect nothing.
 *
 * Returns `null` for any token that is not `unknown`, so a caller can append it unconditionally.
 */
export function unknownTokenHint(token: TokenOut): string | null {
  if (token.letter !== UNKNOWN) return null;
  const raw = token.raw.replace(/[{}[\]()]/g, '');
  if (raw === 's') {
    return 'sibilant, series undetermined — the editor wrote a bare s, so this is a letter but not a reading of s¹ or s². Box it, and note which sibilant you see';
  }
  return `the source has "${token.raw}", which has no letter identity — most likely a typographic mark rather than a glyph. Box it only if you can see one`;
}

/** The token indices already claimed by a box. */
export function claimedTokenIndices(boxes: readonly DraftBox[]): Set<number> {
  const claimed = new Set<number>();
  for (const box of boxes) {
    if (box.alignment === 'expected' && box.expectedTokenIndex !== null) {
      claimed.add(box.expectedTokenIndex);
    }
  }
  return claimed;
}

/** A token is *resolved* when it is boxed or explicitly recorded as not present. */
export function resolvedTokenIndices(
  boxes: readonly DraftBox[],
  absent: ReadonlySet<number>,
): Set<number> {
  const resolved = claimedTokenIndices(boxes);
  for (const index of absent) resolved.add(index);
  return resolved;
}

export interface Assignment {
  letterClass: number;
  letter: string | null;
  display: string | null;
  alignment: Alignment;
  expectedTokenIndex: number | null;
  wordIndex: number | null;
  uncertain: boolean;
  restored: boolean;
  /** Plain-language reason, shown in the UI when the box did not become the expected letter. */
  note: string | null;
}

export interface AssignmentContext {
  tokens: readonly TokenOut[];
  boxes: readonly DraftBox[];
  cursor: number;
  /** The annotator asked for an insertion explicitly (the "insert unexpected letter" mode). */
  forceUnexpected?: boolean;
  /** Class used when there is no expected letter to assign — `unknown` from the vocabulary. */
  fallbackClass: VocabEntryOut | null;
}

/**
 * What letter a box drawn right now should get.
 *
 * Never types, never opens a menu: the current chip is the answer. The only branches are the
 * two honest ones — the chip is already claimed (insertion), or there is no chip at all.
 */
export function assignmentForCursor(ctx: AssignmentContext): Assignment {
  const token = ctx.tokens[ctx.cursor];
  const fallback: Assignment = {
    letterClass: ctx.fallbackClass?.id ?? 0,
    letter: ctx.fallbackClass?.letter ?? null,
    display: ctx.fallbackClass?.display ?? null,
    alignment: 'unexpected',
    expectedTokenIndex: null,
    wordIndex: null,
    uncertain: false,
    restored: false,
    note:
      ctx.tokens.length === 0
        ? 'This inscription has no transliteration, so every box is an unexpected letter. Set its class in the inspector.'
        : 'The cursor is past the last expected letter. This box is recorded as an unexpected letter.',
  };
  if (!token) return fallback;

  const claimed = claimedTokenIndices(ctx.boxes);
  const alreadyClaimed = claimed.has(token.index);

  if (ctx.forceUnexpected || alreadyClaimed) {
    return {
      letterClass: token.letter_class,
      letter: token.letter,
      display: token.display,
      alignment: 'unexpected',
      expectedTokenIndex: null,
      wordIndex: token.word_index,
      // A scholar's {} / [] marking belongs to the token they marked. An extra glyph the
      // annotator found in the same stretch is theirs, not the scholar's, so the flags are
      // not copied onto it.
      uncertain: false,
      restored: false,
      note: ctx.forceUnexpected
        ? `Recorded as an unexpected letter: a glyph the transliteration does not contain.`
        : isLacunaToken(token)
          ? `The illegible stretch already has a box. This glyph is recorded as an unexpected letter — one chip can only be one box, and a lacuna stands for an unknown number of glyphs.`
          : `Expected letter ${token.index + 1} is already boxed. This box is recorded as an unexpected letter.`,
    };
  }

  return {
    letterClass: token.letter_class,
    letter: token.letter,
    display: token.display,
    alignment: 'expected',
    expectedTokenIndex: token.index,
    wordIndex: token.word_index,
    // {} and [] arrive pre-flagged. `{ṣ}` is the class `ṣ` with `uncertain=true` — never a
    // separate class.
    uncertain: token.uncertain,
    restored: token.restored,
    note: null,
  };
}

export function boxFromAssignment(assignment: Assignment, bbox: OrientedRect): DraftBox {
  const chirality = chiralityForClass(assignment.letterClass);
  return {
    key: newKey(),
    id: null,
    letterClass: assignment.letterClass,
    letter: assignment.letter,
    display: assignment.display,
    bbox,
    alignment: assignment.alignment,
    expectedTokenIndex: assignment.expectedTokenIndex,
    wordIndex: assignment.wordIndex,
    // The deprecated boolean, and no longer a claim about anything. It used to be
    // `suggestMirrored(readingDirection)`, which is exactly the derivation this replaces —
    // so a new box carries the column's own default and says nothing, while `chirality`
    // says the true thing: nobody has looked.
    mirrored: mirroredForChirality(chirality, false),
    chirality,
    uncertain: assignment.uncertain,
    restored: assignment.restored,
    source: 'human',
    // Non-negotiable: a human box carries no confidence. The API rejects one that does.
    confidence: null,
    notes: null,
  };
}

// ---------------------------------------------------------------------------
// Cursor movement
// ---------------------------------------------------------------------------

export interface CursorContext {
  tokens: readonly TokenOut[];
  resolved: ReadonlySet<number>;
  cursor: number;
}

/**
 * Where the cursor goes after a box is drawn.
 *
 * Sticky on a lacuna chip — see the module docstring. Otherwise it advances to the next
 * *unresolved* chip, wrapping once to pick up anything skipped earlier, and parks at the end
 * when everything is resolved.
 */
export function cursorAfterDraw(ctx: CursorContext): number {
  const token = ctx.tokens[ctx.cursor];
  if (isLacunaToken(token)) return ctx.cursor;
  return nextUnresolved(ctx);
}

export function nextUnresolved(ctx: CursorContext): number {
  const { tokens, resolved, cursor } = ctx;
  if (tokens.length === 0) return 0;
  for (let index = cursor + 1; index < tokens.length; index += 1) {
    if (!resolved.has(index)) return index;
  }
  for (let index = 0; index <= cursor && index < tokens.length; index += 1) {
    if (!resolved.has(index)) return index;
  }
  return tokens.length; // everything resolved: park past the end.
}

/** Plain step, for the arrow keys. Clamped, never wrapping — wrapping arrows disorient. */
export function stepCursor(tokenCount: number, cursor: number, delta: number): number {
  if (tokenCount === 0) return 0;
  return Math.min(Math.max(cursor + delta, 0), tokenCount - 1);
}

// ---------------------------------------------------------------------------
// Chirality
//
// This replaced `suggestMirrored`, which returned `direction === 'ltr'` and was the default
// for every box's `mirrored` flag. That made "I judged this not mirrored" and "I never
// looked" the same stored byte, and on a record where nobody overrode it the column was
// 100% derivable from `inscriptions.reading_direction` — it carried no information at all.
//
// **Nothing here derives a chirality from a reading direction, from `mirrored`, or from
// anything else about the record.** The one thing that is decided without a human is
// `symmetric`, and it comes from the *measured* table in the glyph artifact: a form that is
// its own mirror has no handedness to record, so there is no question to ask. That is a
// measurement, not an assumption.
// ---------------------------------------------------------------------------

/**
 * What a box on this class is born as.
 *
 * Three cases, from `letterChirality`'s three-valued disposition:
 *
 *  * **symmetric** — the two candidate forms are the same picture, so the answer is settled by
 *    measurement and stored without anybody being asked. `symmetric` is "no work needed", not
 *    "work done, no answer"; merging it into `indeterminate` would count roughly a quarter of
 *    this corpus's letter tokens as outstanding work that can never be completed.
 *  * **chiral** — `null`. Nobody has looked, and that is never a negative example.
 *  * **unmeasured** (`illegible`, `unknown` — no reference form exists) — also `null`, but for
 *    a different reason, and the difference matters at the picker: there is no artifact frame
 *    for `reference`/`mirrored` to name, so those two options are refused rather than offered.
 */
export function chiralityForClass(letterClass: number): Chirality | null {
  return isSymmetric(letterClass) ? 'symmetric' : null;
}

/**
 * The deprecated `mirrored` boolean to store alongside a chirality.
 *
 * **One-way, and only where a boolean can express the value at all.** This mirrors
 * `MIRRORED_EQUIVALENT` in `hismaic/db/models.py`, which is the authoritative statement of the
 * relationship; the two entries below are the whole of it. `null` ("nobody has looked"),
 * `symmetric` ("there is no answer") and `indeterminate` ("looked, could not tell") have no
 * boolean counterpart, so the current value is kept rather than invented.
 *
 * The other direction does not exist and must not be added. A `mirrored: false` is very
 * largely the deleted `suggestMirrored` default, so reading a chirality out of it would
 * manufacture a judgement from an assumption (D4).
 *
 * **Why the client does this rather than the service.** The service deliberately keeps the two
 * columns independent — `tests/test_api_annotations.py::test_patching_chirality_does_not_touch_mirrored`
 * pins it — because the dataset card's `mirrored disagreeing with chirality` row is the only
 * instrument that can see a *server-side* write path updating one and not the other, and a
 * service that silently reconciled them would blind it. The SPA is the caller that stopped
 * stating a meaningful `mirrored` when `suggestMirrored` was deleted, so the SPA is where the
 * restatement belongs.
 */
export function mirroredForChirality(chirality: Chirality | null, current: boolean): boolean {
  if (chirality === 'reference') return false;
  if (chirality === 'mirrored') return true;
  return current;
}

/**
 * The order `M` steps through: `null → reference → mirrored → indeterminate → null`.
 *
 * `symmetric` is **not** in the cycle. On a symmetric class `M` refuses outright (see
 * `chiralityCycleRefusal`) rather than skipping the pair, because skipping would leave
 * `symmetric → indeterminate → symmetric`, and on a symmetric letter "work done, no answer"
 * is *definitionally* `symmetric` — two spellings of one fact on a column whose entire
 * purpose is that its values are distinguishable. A cycle with one reachable state is not a
 * cycle.
 *
 * A box that *is* `symmetric` on a chiral class (a human overturned the measurement through
 * the picker) steps to `reference`, so the cycle stays total wherever it is reachable at all.
 */
export function nextChirality(current: Chirality | null): Chirality | null {
  switch (current) {
    case null:
    case 'symmetric':
      return 'reference';
    case 'reference':
      return 'mirrored';
    case 'mirrored':
      return 'indeterminate';
    default:
      return null;
  }
}

/**
 * Why `M` declines on this class, or `null` when it does not.
 *
 * Both refusals are about the *class*, never about the box, and both leave the picker fully
 * usable: the keyboard shortcut is the fast path, and refusing it is not the same as refusing
 * the judgement. Somebody who can see a mirrored glyph must still be able to say so.
 */
export function chiralityCycleRefusal(letterClass: number): string | null {
  const disposition = letterChirality(letterClass);
  if (disposition === 'symmetric') {
    return 'This letter form reads the same either way, so there is no handedness to record. A box on it is stored symmetric without anybody being asked — use the picker if you disagree with that measurement.';
  }
  if (disposition === null) {
    return 'There is no reference form for this class, so there is nothing for “reference” or “mirrored” to be measured against. Record indeterminate from the picker if you looked and could not tell.';
  }
  return null;
}

/**
 * The five options the picker offers, in the order `M` steps through them, with `symmetric`
 * last because nothing steps to it.
 *
 * A table rather than markup so the DOM test can iterate it, and so the two *candidate forms*
 * are stated once. `form` is the mirror flag to draw the reference PNG with, **in the
 * artifact's own frame** — `false` is the stored form, `true` its mirror. It is deliberately
 * not composed with `glyphOrientation(readingDirection)`: every other `GlyphImage` on this
 * screen shows the *record's* frame, but these two are the two things the annotator is
 * choosing between, and composing them would swap the labels on an `ltr` record. That is the
 * derivation D4 deletes, reintroduced as a rendering bug.
 */
export interface ChiralityOption {
  value: Chirality | null;
  /** The option's own name. Also the `GlyphImage` alt text, which is otherwise identical for
   * the two candidates and leaves them indistinguishable to a screen reader. */
  label: string;
  /** One line of plain language, shown beside the label — the glyph may be switched off. */
  hint: string;
  /** Mirror flag for the candidate form, in the artifact's frame; `null` draws no form. */
  form: boolean | null;
}

export const CHIRALITY_OPTIONS: readonly ChiralityOption[] = [
  {
    value: null,
    label: 'not assessed',
    hint: 'nobody has looked at this box — never a negative example',
    form: null,
  },
  {
    value: 'reference',
    label: 'reference',
    hint: 'matches the stored reference form',
    form: false,
  },
  { value: 'mirrored', label: 'mirrored', hint: 'the mirror of it', form: true },
  {
    value: 'indeterminate',
    label: 'indeterminate',
    hint: 'you looked; the glyph is too worn to tell',
    form: null,
  },
  {
    value: 'symmetric',
    label: 'symmetric',
    hint: 'the form reads the same either way, so there is no answer to give',
    form: null,
  },
];

/**
 * Which way the expected-letter strip runs for a record.
 *
 * Two booleans, because the chip strip is a line of writing and a line of writing has two
 * degrees of freedom: whether it runs across or down (`vertical`), and whether it runs against
 * the order the chips are written in the document (`reversed`).
 *
 * | direction | vertical | reversed | `flex-direction` |
 * | --- | --- | --- | --- |
 * | `ltr` | no | no | `row` |
 * | `rtl` | no | yes | `row-reverse` |
 * | `ttb` | yes | no | `column` |
 * | `btt` | yes | yes | `column-reverse` |
 * | `angle`, unset | no | no | `row` |
 *
 * **This is deliberately not `glyphOrientation`, and the two must not be folded together.**
 * They measure from different references. The stored PNG's reference orientation is **`rtl`**,
 * so `rtl` leaves the glyph alone; the DOM's is **left-to-right and top-to-bottom**, so `rtl`
 * reverses the strip. The pair disagrees on exactly the two middle rows:
 *
 * | direction | glyph mirrored | strip reversed |
 * | --- | --- | --- |
 * | `ltr` | yes | no |
 * | `rtl` | no | yes |
 * | `ttb` | no | no |
 * | `btt` | yes | yes |
 *
 * A shared `{mirrored, turned}` would therefore be right on `ltr` and `btt` and wrong on the
 * other two — the kind of abstraction that looks like a simplification and is a bug.
 *
 * An `angle` record and an unanswered one both lay out left to right. A baseline's slope is not
 * a quarter turn, and an unset direction is a question nobody has answered — the strip stays in
 * the order the tokens are in, which is the one thing that is true regardless.
 */
export interface ReadingFlow {
  /** The sequence stacks instead of running across. */
  vertical: boolean;
  /** It runs against the document's own order — right to left, or bottom to top. */
  reversed: boolean;
}

export function readingFlow(direction: ReadingDirection | null): ReadingFlow {
  switch (direction) {
    case 'rtl':
      return { vertical: false, reversed: true };
    case 'ttb':
      return { vertical: true, reversed: false };
    case 'btt':
      return { vertical: true, reversed: true };
    default:
      return { vertical: false, reversed: false };
  }
}

export function directionLabel(direction: ReadingDirection | null): string {
  switch (direction) {
    case 'ltr':
      return 'left to right';
    case 'rtl':
      return 'right to left';
    case 'ttb':
      return 'top to bottom';
    case 'btt':
      return 'bottom to top';
    case 'angle':
      return 'angled baseline';
    default:
      return 'not set';
  }
}

// ---------------------------------------------------------------------------
// Server <-> editor conversion
// ---------------------------------------------------------------------------

export function boxFromAnnotation(row: AnnotationOut): DraftBox {
  return {
    key: newKey(),
    id: row.id,
    letterClass: row.letter_class,
    letter: row.letter,
    display: row.display,
    // The service always states `kind`; `angle` is null for an upright box, which is the
    // same geometry as zero but a different statement, and `rectToOriented` keeps the two
    // apart by carrying the discriminator rather than deriving it.
    bbox: {
      x: row.bbox.x,
      y: row.bbox.y,
      w: row.bbox.w,
      h: row.bbox.h,
      angle: row.bbox.angle ?? 0,
      kind: row.bbox.kind ?? 'aabb',
    },
    alignment: row.alignment,
    expectedTokenIndex: row.expected_token_index,
    wordIndex: row.word_index,
    mirrored: row.mirrored,
    // Verbatim, including `null`. Never `?? 'reference'` and never coerced to a boolean:
    // "nobody has looked" is a value on this column, not a missing one.
    chirality: row.chirality,
    uncertain: row.uncertain,
    restored: row.restored,
    source: row.source,
    confidence: row.confidence,
    notes: row.notes,
  };
}

/**
 * Relabel a box onto a different chip.
 *
 * If that chip is already taken by another box the relabel still happens, but as an insertion:
 * the class changes and the alignment becomes `unexpected`. Silently stealing the chip from
 * the other box, or refusing outright, would both be worse — the first loses a record, the
 * second blocks a legitimate correction.
 */
export function relabelBox(
  box: DraftBox,
  token: TokenOut,
  boxes: readonly DraftBox[],
): { box: DraftBox; note: string | null } {
  const owner = boxes.find(
    (other) =>
      other.key !== box.key &&
      other.alignment === 'expected' &&
      other.expectedTokenIndex === token.index,
  );
  if (owner) {
    return {
      box: {
        ...box,
        letterClass: token.letter_class,
        letter: token.letter,
        display: token.display,
        alignment: 'unexpected',
        expectedTokenIndex: null,
        wordIndex: token.word_index,
        // The scholar's {} / [] markers belong to the token they marked. This box is no longer
        // that token, so carrying the flags over would put a scholar's mark of uncertainty on a
        // letter they never marked. Same rule the draw path already follows.
        ...clearedScholarFlags(),
        ...chiralityFields(box, token.letter_class),
      },
      note: `Expected letter ${token.index + 1} is already boxed, so this box now carries the class ${token.display} as an unexpected letter${scholarFlagNote(box)}.`,
    };
  }
  return {
    box: {
      ...box,
      letterClass: token.letter_class,
      letter: token.letter,
      display: token.display,
      alignment: 'expected',
      expectedTokenIndex: token.index,
      wordIndex: token.word_index,
      uncertain: token.uncertain,
      restored: token.restored,
      ...chiralityFields(box, token.letter_class),
    },
    note: `Relabelled onto expected letter ${token.index + 1} (${token.display}).${
      token.uncertain || token.restored
        ? ` It is pre-flagged ${[token.uncertain ? 'uncertain' : null, token.restored ? 'restored' : null]
            .filter(Boolean)
            .join(' and ')} from OCIANA's reading.`
        : ''
    }`,
  };
}

// ---------------------------------------------------------------------------
// What a click on a chip means
// ---------------------------------------------------------------------------

export type ChipAction = 'move-cursor' | 'relabel-selected';

export interface ChipGesture {
  /** `'contextmenu'` is the right-click relabel; anything else is an ordinary activation. */
  kind: 'click' | 'contextmenu';
  shiftKey?: boolean;
}

/**
 * Resolve a chip gesture to an action. **A plain click never changes a letter's identity.**
 *
 * This lived inline in `TokenStrip.svelte` as
 * `if (session.selected) relabel(); else moveCursor();` — and because `addBox` selects the box
 * it has just drawn, a box is selected essentially always. The commonest gesture in the tool
 * (draw a box, click a chip further along to jump ahead) therefore relabelled the box just
 * drawn onto the clicked chip and released its claim on the correct one, saying nothing at all
 * when the target chip happened to be free. That is a silent letter-identity change on
 * training data.
 *
 * It is a function, in this file, so that policy is pinned by a test rather than by a line of
 * markup nobody can assert against.
 */
export function chipAction(gesture: ChipGesture, hasSelection: boolean): ChipAction {
  if (!hasSelection) return 'move-cursor';
  if (gesture.kind === 'contextmenu') return 'relabel-selected';
  return gesture.shiftKey ? 'relabel-selected' : 'move-cursor';
}

/**
 * The chirality a box carries once its **letter class** changes.
 *
 * A box relabelled from `n` (symmetric) to `b` (chiral) would otherwise keep the `symmetric`
 * its old class was born with — a claim about a form nobody measured that way, and a silent
 * one: `symmetric` is excluded from the chirality head's training set, so the box would drop
 * out of it without any human ever having said anything. The reverse leaves a chiral `null` on
 * a class that will never be asked about.
 *
 * So the *machine-set* values — `null` and the born `symmetric` — are re-derived from the new
 * class. A human's `reference`, `mirrored` or `indeterminate` is left exactly where it is,
 * even when it now sits on a symmetric class: they looked at a glyph and said something, and
 * that is a finding for `db/chirality_report.py` to surface, not a value for a relabel to
 * quietly erase. This is not a derivation of a *judgement* (D4) — it is the same measured
 * table `chiralityForClass` reads at birth, applied when the thing it keys on changes.
 *
 * **One case this cannot distinguish, stated rather than glossed.** A human may set `symmetric`
 * through the picker — D3 allows overturning the measured table — and a human's `symmetric` is
 * byte-identical to the machine's. So relabelling a box a person deliberately marked `symmetric`
 * drops it to `null`, losing that judgement. The error runs in the safe direction, by the same
 * asymmetry migration `0006` reasons from: dropping a true judgement is recoverable, because the
 * box can be judged again, while inventing a false one is not — the chirality head trains on
 * `reference`/`mirrored` alone and no per-class table can see a manufactured value. Recording a
 * provenance bit to tell the two apart would cost a column to save a re-click.
 */
export function chiralityAfterClassChange(
  current: Chirality | null,
  letterClass: number,
): Chirality | null {
  return current === null || current === 'symmetric' ? chiralityForClass(letterClass) : current;
}

/** `chirality` + the deprecated boolean that follows it, for a box changing class. */
function chiralityFields(
  box: DraftBox,
  letterClass: number,
): Pick<DraftBox, 'chirality' | 'mirrored'> {
  const chirality = chiralityAfterClassChange(box.chirality, letterClass);
  return { chirality, mirrored: mirroredForChirality(chirality, box.mirrored) };
}

/**
 * The flags a box loses when it stops being an expected letter.
 *
 * `uncertain` and `restored` are the *scholar's* statement about a token in the
 * transliteration, adopted by a box only for as long as that box claims to be that token.
 * A box demoted to an insertion is the annotator's own find, so the markers come off.
 */
export function clearedScholarFlags(): Pick<DraftBox, 'uncertain' | 'restored'> {
  return { uncertain: false, restored: false };
}

function scholarFlagNote(box: DraftBox): string {
  if (!box.uncertain && !box.restored) return '';
  const which = [box.uncertain ? 'uncertain' : null, box.restored ? 'restored' : null]
    .filter(Boolean)
    .join(' and ');
  return `; the editor's ${which} marking was cleared because it belongs to the chip, not to this glyph`;
}

/**
 * Set an explicit vocabulary class on a box.
 *
 * This is the path for the third disagreement case: a glyph on the stone whose letter does not
 * occur anywhere in this inscription's transliteration, so no chip carries the class. It was
 * previously unreachable, which made "an insertion is first-class" untrue for exactly the
 * insertions that matter most.
 *
 * An `expected` box that is given a class other than its chip's stops being that chip's
 * letter: it is the annotator saying "OCIANA reads X here, the stone has Y". That is an
 * insertion plus (usually) an absence, and it is recorded as an insertion here — never by
 * quietly leaving the box attached to a token whose class it no longer carries.
 */
export function setBoxClass(
  box: DraftBox,
  entry: VocabEntryOut,
  tokens: readonly TokenOut[],
): { box: DraftBox; note: string | null } {
  const token = box.expectedTokenIndex !== null ? tokens[box.expectedTokenIndex] : undefined;
  const stillMatchesChip = token !== undefined && token.letter_class === entry.id;

  if (box.alignment === 'expected' && !stillMatchesChip) {
    return {
      box: {
        ...box,
        letterClass: entry.id,
        letter: entry.letter,
        display: entry.display,
        alignment: 'unexpected',
        expectedTokenIndex: null,
        ...clearedScholarFlags(),
        ...chiralityFields(box, entry.id),
      },
      note:
        token !== undefined
          ? `Expected letter ${token.index + 1} reads ${token.display}; this box is now the class ${entry.display} and is recorded as an unexpected letter. Mark chip ${token.index + 1} not present (N) if the stone does not show it.`
          : `Class set to ${entry.display}; the box is recorded as an unexpected letter.`,
    };
  }

  if (box.letterClass === entry.id) return { box, note: null };

  return {
    box: {
      ...box,
      letterClass: entry.id,
      letter: entry.letter,
      display: entry.display,
      ...chiralityFields(box, entry.id),
    },
    note: `Class set to ${entry.display}.`,
  };
}
